/**
 * Shared Chat API Service
 *
 * This service provides chat functionality for both worker and company frontends.
 * Uses webapp endpoints that work with jobAssignmentId or jobChatId.
 */

import { privateApi } from '@services/api';
import { ChatMessagesResponse } from '@th-types/chat.type';
import MessageRequest from '@th-types/message-request.type';
import IResultResponse from '@th-types/resultResponse.type';

const CHAT_URL = 'chat';

/**
 * Fetches messages for a specific job assignment/chat
 * Uses webapp endpoint: /api/webapp/chat/job-chats/{id}/messages
 *
 * @param jobAssignmentIdOrChatId Can be either jobAssignmentId or jobChatId
 */
export const fetchChatMessages = async (
  jobAssignmentIdOrChatId: number | null | undefined
): Promise<ChatMessagesResponse> => {
  if (!jobAssignmentIdOrChatId) {
    return Promise.reject(new Error('No job assignment/chat ID provided'));
  }

  const result = await privateApi.get<ChatMessagesResponse>(
    `${CHAT_URL}/job-chats/${jobAssignmentIdOrChatId}/messages`
  );
  return result.data;
};

/**
 * Sends a message to a job chat
 * Uses webapp endpoint: /api/webapp/chat/job-chats/{id}/send
 *
 * @param jobAssignmentIdOrChatId Can be either jobAssignmentId or jobChatId
 * @param message The message content to send
 */
export const sendChatMessage = async (
  jobAssignmentIdOrChatId: number | null,
  message: MessageRequest
): Promise<IResultResponse> => {
  if (!jobAssignmentIdOrChatId) {
    return {
      success: false,
      status: 400,
      message: 'No job assignment/chat ID provided',
    };
  }

  const result = await privateApi.post<IResultResponse>(
    `${CHAT_URL}/job-chats/${jobAssignmentIdOrChatId}/send`,
    message
  );
  return result.data;
};

/**
 * Marks all messages in a chat as read
 * Uses webapp endpoint: /api/webapp/chat/job-chats/{id}/read
 *
 * @param jobAssignmentIdOrChatId Can be either jobAssignmentId or jobChatId
 */
export const markChatMessagesAsRead = async (
  jobAssignmentIdOrChatId: number | null | undefined
): Promise<IResultResponse> => {
  if (!jobAssignmentIdOrChatId) {
    return Promise.reject(new Error('No job assignment/chat ID provided'));
  }

  const result = await privateApi.post<IResultResponse>(
    `${CHAT_URL}/job-chats/${jobAssignmentIdOrChatId}/read`
  );
  return result.data;
};

/**
 * Marks messages in a chat as unread
 * Uses webapp endpoint: /api/webapp/chat/job-chats/{id}/unread
 *
 * @param jobChatId The job chat ID
 */
export const markChatMessagesAsUnread = async (
  jobChatId: number | null | undefined
): Promise<IResultResponse> => {
  if (!jobChatId) {
    return Promise.reject(new Error('No job chat ID provided'));
  }

  const result = await privateApi.post<IResultResponse>(
    `${CHAT_URL}/job-chats/${jobChatId}/unread`
  );
  return result.data;
};
